import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { PathologyTrackingEventForm } from '@/api/dtos';
import { Box } from '@mui/material';
import { ValidateOn } from '@ngt/forms-core';
import {
    Checkbox,
    CollaboratingGroupContext,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    FormDefinitionContext,
    GroupedFields,
    GroupedTableArrayField,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, useContext, useMemo } from 'react';

const PathologyTrackingFields: FC = () => {
    const columns = useMemo<Array<IGroupedTableArrayFieldColumn<PathologyTrackingEventForm>>>(() => [
        {
            propertyPath: 'timePoint',
            content: <Input component={Select} />,
        },
        {
            propertyPath: 'timePointSpecify',
            content: <Input component={Text} />,
        },
        {
            propertyPath: 'sampleType',
            content: <Input component={Text} />,
        },
        {
            propertyPath: 'date',
            content: <Input component={DatePicker} />,
        },
        {
            propertyPath: 'notDone',
            content: <Input component={Checkbox} />,
        },
        {
            propertyPath: 'comments',
            content: <Input component={Text} />,
        },
        {
            propertyPath: 'doNotPay',
            content: <Input component={Checkbox} />,
        },
    ], []);

    return (
        <GroupedFields>
            <GroupedTableArrayField<PathologyTrackingEventForm>
                propertyPath="pathologyTrackingEvents"
                columns={columns}
                AddButtonProps={{ sx: { mb: 2 } }}
            />
        </GroupedFields>
    );
};

const PathologyTrackingForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                debug={AppSettings.debug}
                AdditionalContent={{
                    beforeChildren: formDefinition?.instructions ?
                        <Box
                            sx={{
                                p: 2,
                            }}
                        >
                            {formDefinition?.instructions}
                        </Box> :
                        undefined
                }}
            >
                <PathologyTrackingFields />
            </CrfForm>
        </PageLayout>
    );
};

export default PathologyTrackingForm;
